import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera } from 'lucide-react';

interface BarcodeScannerProps {
  onScanSuccess: (barcode: string) => void;
  onClose: () => void;
}

export function BarcodeScanner({ onScanSuccess, onClose }: BarcodeScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const hasScannedRef = useRef(false);

  useEffect(() => {
    let scanner: Html5Qrcode | null = null;

    async function startScanning() {
      try {
        // Create scanner instance (without UI)
        scanner = new Html5Qrcode('barcode-scanner-container');
        scannerRef.current = scanner;

        // Get list of cameras
        const devices = await Html5Qrcode.getCameras();
        
        if (devices && devices.length > 0) {
          // Find back camera (environment-facing)
          let selectedCamera = devices[0]; // Default to first camera
          
          // Look for back/rear camera
          for (const device of devices) {
            const label = device.label.toLowerCase();
            if (label.includes('back') || label.includes('rear') || label.includes('environment')) {
              selectedCamera = device;
              break;
            }
          }
          
          // If multiple cameras and no explicit back camera found, use the last one
          // (back cameras are usually listed last)
          if (devices.length > 1 && selectedCamera === devices[0]) {
            selectedCamera = devices[devices.length - 1];
          }

          console.log('Using camera:', selectedCamera.label);

          // Start scanning with selected camera
          await scanner.start(
            selectedCamera.id,
            {
              fps: 10,
              qrbox: { width: 250, height: 250 },
              aspectRatio: 1.0,
            },
            (decodedText) => {
              // Prevent duplicate scans
              if (hasScannedRef.current) return;
              hasScannedRef.current = true;
              
              console.log('Barcode scanned:', decodedText);
              
              // Call success callback immediately
              onScanSuccess(decodedText);
              
              // Stop and clean up scanner
              if (scanner && scanner.isScanning) {
                scanner.stop().then(() => {
                  scanner?.clear();
                  // Let AddItem's setShowScanner(false) handle closing
                }).catch(console.error);
              }
            },
            (errorMessage) => {
              // Error callback - ignore "not found" errors
              if (!errorMessage.includes('NotFoundException')) {
                console.warn('Scan error:', errorMessage);
              }
            }
          );

          setIsScanning(true);
        } else {
          setError('No cameras found on this device');
        }
      } catch (err: any) {
        console.error('Error starting scanner:', err);
        setError(err.message || 'Failed to start camera');
      }
    }

    startScanning();

    // Cleanup on unmount
    return () => {
      if (scanner && scanner.isScanning) {
        scanner.stop().then(() => {
          scanner?.clear();
          // Clean up any remaining scanner elements
          const container = document.getElementById('barcode-scanner-container');
          if (container) {
            container.innerHTML = '';
          }
        }).catch(err => {
          console.error('Error stopping scanner:', err);
          // Force cleanup even on error
          const container = document.getElementById('barcode-scanner-container');
          if (container) {
            container.innerHTML = '';
          }
        });
      }
    };
  }, [onScanSuccess]);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-mint rounded-full mb-4">
            <Camera className="w-8 h-8 text-primary-green" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Scan Barcode
          </h2>
          <p className="text-gray-600">
            Position the barcode within the camera frame
          </p>
        </div>

        {/* Scanner container */}
        <div id="barcode-scanner-container" className="mb-4 rounded-xl overflow-hidden"></div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 text-red-800 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Loading state */}
        {!isScanning && !error && (
          <div className="bg-light-mint p-4 rounded-xl flex items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-green border-t-transparent"></div>
            <span className="text-primary-green font-medium">Starting camera...</span>
          </div>
        )}

        {/* Instructions */}
        {isScanning && (
          <div className="bg-light-mint p-4 rounded-xl">
            <p className="text-sm text-gray-700 text-center">
              <strong>Tip:</strong> Hold your camera steady and ensure good lighting for best results
            </p>
          </div>
        )}

        {/* Cancel button */}
        <button
          onClick={onClose}
          className="w-full mt-4 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
